import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  FILE_ATTACHMENT_SERVICE,
  IMAGE_ASSET_SERVICE,
} from '@advinsoftwares/ngx-rich-text-editor';
import { AdvErpImageAssetService } from './adverp-image-asset.service';
import { AdvErpFileAttachmentService } from './adverp-file-attachment.service';

/**
 * Shared rich-text-editor wrapper used across AdvERP (email templates,
 * white-label content, bulk mail, notification bodies, etc.). This is the
 * same public API the previous CuteEditor-backed version exposed — a
 * required `control` FormControl input and an `onBlur` output — so none
 * of the consuming templates ([control]="someForm.controls.aBody") need
 * to change. Only what's inside changed: the editor itself is now
 * `<rte-editor>` from @advinsoftwares/ngx-rich-text-editor (a custom
 * Angular editor built in-house, see its GUIDE.md), and image/attachment
 * uploads are wired to AdvERP's real DocumentMaster_Upload endpoint via
 * the two adapter services below (see those files for why list()/delete()
 * are no-ops for now — parity with what CuteEditor's upload-only flow did,
 * not a regression).
 *
 * `[formControl]="control"` binds directly to the FormControl instance
 * passed in from the parent — `<rte-editor>` implements
 * ControlValueAccessor, so no manual valueChanges subscription is needed
 * here the way the old CuteEditor wrapper required.
 *
 * `(focusout)` (not `(blur)`) is used for the onBlur output because it
 * bubbles: a plain `(blur)` on the host wouldn't fire when focus moves
 * from, say, the editor's contenteditable surface to one of its own
 * toolbar buttons. No consumer currently binds `(onBlur)` on this
 * component (checked across all 6 usage sites), so this is a safe,
 * behavior-preserving change rather than a risk.
 */
@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.css'],
  providers: [
    { provide: IMAGE_ASSET_SERVICE, useClass: AdvErpImageAssetService },
    { provide: FILE_ATTACHMENT_SERVICE, useClass: AdvErpFileAttachmentService },
  ],
})
export class RichTextEditorComponent {
  @Input({ required: true }) control!: FormControl;
  @Output() onBlur = new EventEmitter<void>();
}
